import React, { useState, useEffect } from 'react';
import { X, Send, Zap } from 'lucide-react';
import './ExploreDataSourcesModal.css';

function ExploreDataSourcesModal({ isOpen, onClose, onSendCommand, isProcessing, connection }) {
  const [customInstructions, setCustomInstructions] = useState('');
  const [commandPreview, setCommandPreview] = useState('/explore_ds');

  // Build the base command with connection context
  const getBaseCommand = () => {
    if (connection?.id) {
      return `/explore_ds --connection=${connection.id}`;
    }
    return '/explore_ds';
  };

  // Update command preview as user types or connection changes
  useEffect(() => {
    const baseCmd = getBaseCommand();
    if (customInstructions.trim()) {
      setCommandPreview(`${baseCmd} ${customInstructions}`);
    } else {
      setCommandPreview(baseCmd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customInstructions, connection]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCustomInstructions('');
      setCommandPreview(getBaseCommand());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, connection]);

  const handleSend = () => {
    const baseCmd = getBaseCommand();
    const command = customInstructions.trim()
      ? `${baseCmd} ${customInstructions}`
      : baseCmd;
    onSendCommand(command);
    setCustomInstructions('');
  };

  if (!isOpen) return null;

  return (
    <div className="explore-modal-overlay">
      <div className="explore-modal">
        <div className="explore-modal-header">
          <div className="explore-modal-title">
            <Zap size={20} />
            <span>Explore Data Sources</span>
          </div>
          <button
            className="explore-modal-close"
            onClick={onClose}
            disabled={isProcessing}
          >
            <X size={18} />
          </button>
        </div>

        <div className="explore-modal-content">
          <div className="explore-section">
            <h3>What would you like to explore?</h3>
            <p className="explore-description">
              {connection ? (
                <>Explore the <strong>{connection.name}</strong> data source to discover its structure and understand how to work with the data.</>
              ) : (
                <>The system will discover your database structure, test connections, and help you understand how to work with your data.</>
              )}
            </p>
          </div>

          <div className="explore-section">
            <label className="explore-label">Additional Instructions (Optional)</label>
            <textarea
              className="explore-textarea"
              placeholder="e.g., Focus on the customer and orders tables, show relationships..."
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              disabled={isProcessing}
              rows={4}
            />
            <p className="explore-help-text">
              Provide additional context or specific areas you'd like to explore. Leave blank for a complete database exploration.
            </p>
          </div>

          <div className="explore-section">
            <div className="command-preview">
              <label className="preview-label">Command to send:</label>
              <div className="preview-box">
                <code>{commandPreview}</code>
              </div>
            </div>
          </div>
        </div>

        <div className="explore-modal-footer">
          <button
            className="explore-cancel-button"
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            className="explore-send-button"
            onClick={handleSend}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="spinner" />
                Exploring...
              </>
            ) : (
              <>
                <Send size={16} />
                Start Exploration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExploreDataSourcesModal;
